import React from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Card,
  CardBody,
  Icon,
  Progress,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiInfo } from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'

export interface UsageStatsData {
  used: number
  limit: number | null
  remaining: number | null
  percentage: number | null
  status?: 'ok' | 'warning' | 'exceeded' | null
  tokens: number
}

interface UsageStatsCardProps {
  title: string
  icon: React.ElementType
  stats: UsageStatsData
  colorScheme: string
  showTokens?: boolean
}

export default function UsageStatsCard({
  title,
  icon,
  stats,
  colorScheme,
  showTokens = true,
}: UsageStatsCardProps) {
  const { t, formatNumber } = useAppTranslation('common')
  const progressBg = useColorModeValue('gray.100', 'gray.700')
  const cardBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`)
  const borderColor = useColorModeValue(`${colorScheme}.200`, `${colorScheme}.700`)

  const percentage = stats.percentage ?? 0

  const getStatusColor = () => {
    if (stats.status === 'exceeded' || percentage >= 100) return 'red'
    if (stats.status === 'warning' || percentage >= 80) return 'yellow'
    return colorScheme
  }

  const getStatusText = () => {
    if (stats.status === 'exceeded' || percentage >= 100) return t('account.usage.status.exceeded')
    if (stats.status === 'warning' || percentage >= 80) return t('account.usage.status.nearLimit')
    return t('account.usage.status.available')
  }

  const statusColor = getStatusColor()
  const hasLimit = stats.limit !== null

  return (
    <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} variant="outline" borderRadius="lg">
      <CardBody>
        <VStack align="stretch" spacing={3}>
          {/* Header with icon, title and status badge */}
          <HStack justify="space-between" align="center">
            <HStack spacing={2}>
              <Icon as={icon} color={`${colorScheme}.600`} boxSize={5} />
              <Text fontWeight="medium" color={`${colorScheme}.800`} fontSize="sm">
                {title}
              </Text>
            </HStack>
            {hasLimit && (
              <Badge colorScheme={statusColor} variant="subtle" fontSize="xs">
                {getStatusText()}
              </Badge>
            )}
          </HStack>

          {/* Usage numbers */}
          <Box w="full">
            <HStack justify="space-between" mb={2}>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {formatNumber(stats.used)}
              </Text>
              {hasLimit && (
                <Text fontSize="sm" color="gray.600">
                  {t('account.usage.of')} {formatNumber(stats.limit as number)}
                </Text>
              )}
            </HStack>

            {hasLimit && (
              <>
                {/* Progress bar */}
                <Progress
                  value={Math.min(percentage, 100)}
                  colorScheme={statusColor}
                  bg={progressBg}
                  borderRadius="full"
                  size="sm"
                />

                {/* Usage percentage and remaining */}
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="xs" color="gray.500">
                    {percentage.toFixed(1)}% {t('account.usage.used')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {formatNumber(stats.remaining ?? 0)} {t('account.usage.remaining')}
                  </Text>
                </HStack>
              </>
            )}
          </Box>

          {/* Tokens used (if enabled and tokens > 0) */}
          {showTokens && stats.tokens > 0 && (
            <Tooltip label={t('account.usage.tokensTooltip')}>
              <HStack spacing={1}>
                <Icon as={FiInfo} boxSize={3} color="gray.400" />
                <Text fontSize="xs" color="gray.500">
                  {formatNumber(stats.tokens)} {t('account.usage.tokens')}
                </Text>
              </HStack>
            </Tooltip>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}
